<script lang="ts">
// @ts-nocheck
import {
  computed,
  defineComponent,
  h,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import { pick } from 'lodash-unified'
import JdSelect from '@jdesign-vue/components/select'
import JdTree from '@jdesign-vue/components/tree'
import { useSelect } from './select'
import { useTree } from './tree'
import CacheOptions from './cache-options'

export default defineComponent({
  name: 'JdTreeSelect',
  // disable `JdSelect` inherit current attrs
  inheritAttrs: false,
  props: {
    ...JdSelect.props,
    ...JdTree.props,
    /**
     * @description The cached data of the lazy node, the structure is the same as the data, used to get the label of the unloaded data
     */
    cacheData: {
      type: Array,
      default: () => [],
    },
    showCheckbox: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, context) {
    const { slots, expose } = context

    const trackOpenStartTime = ref<number | null>(null)

    const dispatchCustomTrack = (detail: Record<string, any>) => {
      document.dispatchEvent(
        new CustomEvent('customTrack', {
          detail,
        })
      )
    }

    const select = ref<InstanceType<typeof JdSelect>>()
    const tree = ref<InstanceType<typeof JdTree>>()

    const key = computed(() => props.nodeKey || props.valueKey || 'value')

    const selectProps = useSelect(props, context, { select, tree, key })

    const handleVisibleChange = (visible: boolean) => {
      selectProps.onVisibleChange?.(visible)

      if (visible) {
        trackOpenStartTime.value = Date.now()
        return
      }

      if (trackOpenStartTime.value != null) {
        const duration = Date.now() - trackOpenStartTime.value
        dispatchCustomTrack({
          'data-component-name': 'TreeSelect',
          'data-event-name': 'duration',
          'data-event-content': duration,
        })
        trackOpenStartTime.value = null
      }
    }

    watch(
      () => props.modelValue,
      () => {
        dispatchCustomTrack({
          'data-component-name': 'TreeSelect',
          'data-event-name': 'change',
          'data-event-content': '树形选择',
        })
      },
      { deep: true }
    )
    const { cacheOptions, ...treeProps } = useTree(props, context, {
      select,
      tree,
      key,
    })

    // expose JdTree/JdSelect methods
    const methods = reactive({})
    expose(methods)
    onMounted(() => {
      Object.assign(methods, {
        ...pick(tree.value, [
          'filter',
          'updateKeyChildren',
          'getCheckedNodes',
          'setCheckedNodes',
          'getCheckedKeys',
          'setCheckedKeys',
          'setChecked',
          'getHalfCheckedNodes',
          'getHalfCheckedKeys',
          'getCurrentKey',
          'getCurrentNode',
          'setCurrentKey',
          'setCurrentNode',
          'getNode',
          'remove',
          'append',
          'insertBefore',
          'insertAfter',
        ]),
        ...pick(select.value, ['focus', 'blur']),
      })
    })

    return () =>
      h(
        JdSelect,
        /**
         * 1. The `props` is processed into `Refs`, but `v-bind` and
         * render function props cannot read `Refs`, so use `reactive`
         * unwrap the `Refs` and keep reactive.
         * 2. The keyword `ref` requires `Ref`, but `reactive` broke it,
         * so use function.
         */
        reactive({
          ...selectProps,
          multipleAll: false,
          ref: (ref) => (select.value = ref),
          'data-component-name': 'TreeSelect',
          'data-event-name': 'change',
          'data-event-content': '树形选择',
          onVisibleChange: handleVisibleChange,
        }),
        {
          ...slots,
          default: () => [
            h(CacheOptions, { data: cacheOptions.value }),
            h(
              JdTree,
              reactive({
                ...treeProps,
                ref: (ref) => (tree.value = ref),
              })
            ),
          ],
        }
      )
  },
})
</script>
